import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Signal,
  TemplateRef,
  WritableSignal,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectModule } from '@angular/material/select';
import { TranslatePipe } from '@ngx-translate/core';
import { select, Store } from '@ngrx/store';
import { Dictionary } from '@ngrx/entity/src/models';
import { map, startWith, take } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { features } from '@consts/text.const';
import { isWellsFargoEnvironment } from '@consts/environment.const';
import { IntelligenceModels } from '@models/intelligence.model';
import { OrganizationMembersModels } from '@models/organization-members.model';
import { ActiveOrganization, OrganizationModels } from '@models/organization.model';
import { PermissionGroupModels } from '@models/group.model';
import { PermissionModel } from '@models/permission.model';
import { UserModel } from '@models/user.model';
import { OrganizationUsersActions } from '@states/organization-users/organization-users.action-types';
import { OrganizationUsersSelectors } from '@states/organization-users/organization-users.selector-types';
import * as OrganizationActions from '@states/organization/organization.actions';
import { OrganizationSelectors } from '@states/organization/organization.selector-types';
import { PermissionSelectors } from '@states/permissions/permissions.selector-types';
import { SharedActions } from '@states/shared/shared.action-types';
import { UserSelectors } from '@states/user/user.selector-types';
import {
  ConfirmDialogModel,
  ConfirmDialogService,
  UiButtonComponent,
  UiFilterChipComponent,
  UiNoDataComponent,
  UiSelectedItemsSnackbarComponent,
} from '@ui';
import {
  DatasetTableConfig,
  SelectionChangeEvent,
  SortChangeEvent,
  TableSortState,
  UiDatasetTableComponent,
} from '@ui/lib/ui-kit/ui-dataset-table';
import { Router } from '@angular/router';
import { routerSegments } from '@consts/routes';
import { environment } from 'src/environments/environment';
import { OrganizationService } from '../../../../../../development/organization.service';
import { IntelligenceResultsService } from '../../../../../../services/intelligence-results.service';
import { smartSearch, sortArrByField } from '../../../../../../helpers/common.helpers';
import { PipesModule } from '../../../../../../pipes/pipes.module';
import {
  UserEditModalComponent,
  UserEditModalData,
} from '../../../../../../pages/settings/pages/users/components/user-edit-modal/user-edit-modal.component';
import { UiSearchComponent } from '../../../../../ui-kit/ui-search/ui-search.component';
import { UiTruncatedHtmlComponent } from '../../../../../ui-kit/ui-truncated-html/ui-truncated-html.component';
import { UiUserSettingsDateComponent } from '../../../../../ui-kit/ui-user-settings-date/ui-user-settings-date.component';
import {
  OrganizationMemberCellContext,
  createOrganizationMembersTableConfig,
} from './organization-members-table.util';

/** Fields the panel's search box matches against, mirroring the settings users page. */
const SEARCHABLE_FIELDS: string[] = ['firstname', 'lastname', 'email'];

/**
 * Sort key for the name column: trimmed `firstname` + `lastname`. Rows with no name
 * parts fall back to `email` so the ordering stays stable even though their name cell
 * renders an em dash.
 */
function memberDisplayNameSortKey(member: IntelligenceModels.Organizations.Member): string {
  const name = [member.firstname?.trim(), member.lastname?.trim()].filter(Boolean).join(' ');
  return name || (member.email ?? '').trim();
}

function sortMembersByDisplayName(
  members: IntelligenceModels.Organizations.Member[],
  direction: 'asc' | 'desc',
): IntelligenceModels.Organizations.Member[] {
  const multiplier = direction === 'asc' ? 1 : -1;
  return [...members].sort(
    (a, b) =>
      memberDisplayNameSortKey(a).localeCompare(memberDisplayNameSortKey(b), undefined, {
        sensitivity: 'base',
      }) * multiplier,
  );
}

/**
 * `UserEditModalComponent` reads only `_id`, `email`, `firstname`, `lastname` and
 * `roleBehavior`; `OrganizationMember` extends the large `User` interface, so a
 * minimal object needs the cast.
 */
function toOrganizationMember(
  member: IntelligenceModels.Organizations.Member,
): OrganizationModels.OrganizationMember {
  return {
    _id: member.id,
    email: member.email,
    firstname: member.firstname,
    lastname: member.lastname,
    roleBehavior: member.role ?? UserModel.RolePermissionBehavior.INTERNAL_ONLY,
    groupIds: member.groupIds,
  } as OrganizationModels.OrganizationMember;
}

@Component({
  selector: 'app-organization-members-result',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatSelectModule,
    TranslatePipe,
    PipesModule,
    UiButtonComponent,
    UiDatasetTableComponent,
    UiFilterChipComponent,
    UiNoDataComponent,
    UiSearchComponent,
    UiSelectedItemsSnackbarComponent,
    UiTruncatedHtmlComponent,
    UiUserSettingsDateComponent,
  ],
  templateUrl: './organization-members-result.component.html',
  styleUrls: ['./organization-members-result.component.scss'],
})
export class OrganizationMembersResultComponent {
  private readonly intelligenceResultsService = inject(IntelligenceResultsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly store$ = inject(Store);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly organizationService = inject(OrganizationService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);

  /** Same empty state the settings users page shows, minus its create button. */
  protected readonly noData: typeof features.organizationUsers.noData = features.organizationUsers.noData;

  protected readonly nameCellTemplate = viewChild<TemplateRef<OrganizationMemberCellContext>>('nameCell');
  protected readonly emailCellTemplate = viewChild<TemplateRef<OrganizationMemberCellContext>>('emailCell');
  protected readonly permissionsCellTemplate =
    viewChild<TemplateRef<OrganizationMemberCellContext>>('permissionsCell');
  protected readonly lastLoginCellTemplate =
    viewChild<TemplateRef<OrganizationMemberCellContext>>('lastLoginCell');
  protected readonly membersTable =
    viewChild<UiDatasetTableComponent<IntelligenceModels.Organizations.Member>>('membersTable');

  protected readonly response: Signal<IntelligenceModels.Organizations.MembersResponse | null> = computed(
    () => {
      const result = this.intelligenceResultsService.current();
      if (result?.type !== 'organization-members') {
        return null;
      }
      return result.data as IntelligenceModels.Organizations.MembersResponse;
    },
  );

  protected readonly permissionGroups: Signal<IntelligenceModels.Organizations.PermissionGroup[]> = computed(
    () => this.response()?.permissionGroups ?? [],
  );

  protected readonly permissionGroupNamesById: Signal<Record<string, string>> = computed(() =>
    this.permissionGroups().reduce<Record<string, string>>((names, group) => {
      names[group.id] = group.name;
      return names;
    }, {}),
  );

  // ── Store context for the actions (mirrors the settings users page) ─────────

  private readonly currentUserId: Signal<string | null> = toSignal(
    this.store$.pipe(select(UserSelectors.selectUser)).pipe(map((user) => user?._id ?? null)),
    { initialValue: null as string | null },
  );
  private readonly activeOrganization: Signal<ActiveOrganization | null> = toSignal(
    this.store$.pipe(select(OrganizationSelectors.selectActiveOrganization)),
    { initialValue: null as ActiveOrganization | null },
  );
  private readonly isDeveloper: Signal<boolean> = toSignal(
    this.store$.pipe(select(OrganizationSelectors.isDeveloper)),
    { initialValue: false },
  );
  private readonly canManageUserPermissions: Signal<boolean> = toSignal(
    this.store$.pipe(
      select(
        PermissionSelectors.checkAccessPermissionsOR(
          [PermissionModel.Permissions.OrganizationSettingsManageUserPermissions],
          [],
        ),
      ),
    ),
    { initialValue: false },
  );
  private readonly canRemoveUser: Signal<boolean> = toSignal(
    this.store$.pipe(
      select(
        PermissionSelectors.checkAccessPermissionsOR(
          [PermissionModel.Permissions.OrganizationSettingsRemoveUser],
          [],
        ),
      ),
    ),
    { initialValue: false },
  );
  protected readonly canForceLogoutUser: Signal<boolean> = toSignal(
    this.store$.pipe(
      select(
        PermissionSelectors.checkAccessPermissionsOR(
          [PermissionModel.Permissions.OrganizationSettingsForceLogoutUser],
          [],
        ),
      ),
    ),
    { initialValue: false },
  );
  protected readonly canForcePasswordResetUser: Signal<boolean> = toSignal(
    this.store$.pipe(
      select(
        PermissionSelectors.checkAccessPermissionsOR(
          [PermissionModel.Permissions.OrganizationSettingsForcePasswordResetUser],
          [],
        ),
      ),
    ),
    { initialValue: false },
  );
  /** Full permission-group documents for the edit modal (the tool result only carries id+name). */
  private readonly availableGroups: Signal<PermissionGroupModels.PermissionGroupDocumentBase[]> = toSignal(
    this.store$.pipe(select(OrganizationUsersSelectors.selectPermissionGroups)),
    { initialValue: [] as PermissionGroupModels.PermissionGroupDocumentBase[] },
  );

  private readonly isWellsFargoEnv: boolean = isWellsFargoEnvironment(environment.env);

  protected readonly forceLogoutInProgressByEmail: WritableSignal<Record<string, boolean>> = signal<
    Record<string, boolean>
  >({});
  protected readonly resetPasswordInProgressByEmail: WritableSignal<Record<string, boolean>> = signal<
    Record<string, boolean>
  >({});

  // ── Filters ────────────────────────────────────────────────────────────────
  // Panel-local state: filtering happens client-side over the tool result, so no
  // re-query and nothing written to the URL or to NgRx.

  protected readonly searchQuery: WritableSignal<string> = signal<string>('');
  public readonly groupFilterControl = new FormControl<string[]>([], { nonNullable: true });

  private readonly selectedGroupIds: Signal<string[]> = toSignal(
    this.groupFilterControl.valueChanges.pipe(startWith(this.groupFilterControl.value)),
    { initialValue: this.groupFilterControl.value },
  );

  protected readonly sortState: WritableSignal<TableSortState> = signal<TableSortState>({
    key: '',
    direction: OrganizationMembersModels.SortDirection.None,
  });

  protected readonly members: Signal<IntelligenceModels.Organizations.Member[]> = computed(() => {
    const all = this.response()?.members ?? [];
    const groupIds = this.selectedGroupIds();

    const filteredByGroups = groupIds.length
      ? all.filter((member) => member.groupIds.some((groupId) => groupIds.includes(groupId)))
      : all;

    const filtered = smartSearch<IntelligenceModels.Organizations.Member>(
      filteredByGroups,
      this.searchQuery(),
      SEARCHABLE_FIELDS,
    );

    const { key, direction } = this.sortState();
    if (!key || direction === OrganizationMembersModels.SortDirection.None) {
      return filtered;
    }

    const order = direction === OrganizationMembersModels.SortDirection.Desc ? 'desc' : 'asc';
    if (key === 'lastname') {
      return sortMembersByDisplayName(filtered, order);
    }
    return sortArrByField<IntelligenceModels.Organizations.Member>(
      filtered,
      key as keyof IntelligenceModels.Organizations.Member,
      order,
    );
  });

  // ── Selection ──────────────────────────────────────────────────────────────

  protected readonly selectedMembers: WritableSignal<IntelligenceModels.Organizations.Member[]> = signal<
    IntelligenceModels.Organizations.Member[]
  >([]);
  protected readonly selectedMembersCount: Signal<number> = computed(() => this.selectedMembers().length);
  protected readonly areAllSelectedMembersChecked: WritableSignal<boolean> = signal<boolean>(false);

  protected readonly tableConfig: Signal<DatasetTableConfig<IntelligenceModels.Organizations.Member> | null> =
    computed(() => {
      const nameCellTemplate = this.nameCellTemplate();
      const emailCellTemplate = this.emailCellTemplate();
      const permissionsCellTemplate = this.permissionsCellTemplate();
      const lastLoginCellTemplate = this.lastLoginCellTemplate();

      if (!nameCellTemplate || !emailCellTemplate || !permissionsCellTemplate || !lastLoginCellTemplate) {
        return null;
      }

      return createOrganizationMembersTableConfig({
        nameCellTemplate,
        emailCellTemplate,
        permissionsCellTemplate,
        lastLoginCellTemplate,
        selectedMembersCount: this.selectedMembersCount,
        currentUserId: this.currentUserId,
        canManageUserPermissions: this.canManageUserPermissions,
        canRemoveUser: this.canRemoveUser,
        canForceLogoutUser: this.canForceLogoutUser,
        canForcePasswordResetUser: this.canForcePasswordResetUser,
        forceLogoutInProgressByEmail: this.forceLogoutInProgressByEmail,
        resetPasswordInProgressByEmail: this.resetPasswordInProgressByEmail,
        isWellsFargoEnv: this.isWellsFargoEnv,
        isDeveloper: this.isDeveloper,
      });
    });

  /**
   * Clear filters and selection when the user navigates to a different history entry,
   * so one member query's filters never leak onto the next.
   */
  private lastEntryPosition: number = this.intelligenceResultsService.currentPosition();
  private readonly resetOnNavigation = effect(() => {
    const position = this.intelligenceResultsService.currentPosition();
    if (position === this.lastEntryPosition) {
      return;
    }
    this.lastEntryPosition = position;
    this.resetFilters();
  });

  public constructor() {
    this.resetFilters();
    this.groupFilterControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.clearSelection());
    // The edit modal needs the full permission-group documents, not the id+name
    // pairs the tool result carries.
    this.store$.dispatch(OrganizationUsersActions.getOrganizationPermissionGroups());
  }

  protected onSearchQuery(query: string): void {
    // ui-search echoes its initial value back on init; ignore the no-op emission.
    if (query === this.searchQuery()) {
      return;
    }
    this.searchQuery.set(query);
    this.clearSelection();
  }

  protected onSortChange(event: SortChangeEvent): void {
    const { key, direction } = this.sortState();

    if (event.columnKey !== key) {
      // A→Z first (matches "User name" / email expectations); second click Z→A, third clears.
      this.sortState.set({
        key: event.columnKey,
        direction: OrganizationMembersModels.SortDirection.Asc,
      });
      return;
    }

    switch (direction) {
      case OrganizationMembersModels.SortDirection.Asc:
        this.sortState.set({ key, direction: OrganizationMembersModels.SortDirection.Desc });
        break;
      case OrganizationMembersModels.SortDirection.Desc:
        this.sortState.set({ key: '', direction: OrganizationMembersModels.SortDirection.None });
        break;
      default:
        this.sortState.set({ key, direction: OrganizationMembersModels.SortDirection.Asc });
    }
  }

  protected onSelectionChange(event: SelectionChangeEvent<IntelligenceModels.Organizations.Member>): void {
    this.selectedMembers.set(event.selectedRows);
    this.areAllSelectedMembersChecked.set(event.allSelected);
  }

  protected onRowAction(event: OrganizationMembersModels.DatasetRowActionEvent): void {
    switch (event.actionId) {
      case OrganizationMembersModels.RowActionId.Permissions:
        this.openUserPermissionsPage(event.row);
        break;
      case OrganizationMembersModels.RowActionId.ForceLogout:
        this.forceLogout(event.row);
        break;
      case OrganizationMembersModels.RowActionId.ForceResetPassword:
        this.forceResetPassword(event.row);
        break;
      case OrganizationMembersModels.RowActionId.Edit:
        this.openEditModal(event.row);
        break;
      case OrganizationMembersModels.RowActionId.Delete:
        this.deleteMember(event.row);
        break;
    }
  }

  /** ui-dataset-table exposes no select-all API, so the snackbar's action is a no-op. */
  protected onSelectAll(): void {
    return;
  }

  protected clearSelection(): void {
    this.selectedMembers.set([]);
    this.areAllSelectedMembersChecked.set(false);
    this.membersTable()?.clearSelection();
  }

  protected removeGroupFilter(groupId: string): void {
    this.groupFilterControl.setValue(this.groupFilterControl.value.filter((id) => id !== groupId));
  }

  protected permissionNames(groupIds: string[]): string {
    const namesById = this.permissionGroupNamesById();
    return groupIds
      .map((groupId) => namesById[groupId] ?? '')
      .filter(Boolean)
      .join(', ');
  }

  // ── Row actions ─────────────────────────────────────────────────────────────

  protected forceLogout(member: IntelligenceModels.Organizations.Member): void {
    if (
      !member.email ||
      !this.canForceLogoutUser() ||
      this.forceLogoutInProgressByEmail()[member.email]
    ) {
      return;
    }

    this.confirmDialogService
      .open({
        // ui-confirm-dialog translates these via the `translate` pipe, so pass raw keys.
        type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
        title: 'USERS.CONFIRMATIONS.FORCE_LOGOUT_SINGLE.TITLE',
        msg: 'USERS.CONFIRMATIONS.FORCE_LOGOUT_SINGLE.MSG',
        confirm: 'USERS.CONFIRMATIONS.FORCE_LOGOUT_SINGLE.CONFIRM',
        disableClose: true,
        params: { email: member.email },
      })
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result?.selection !== ConfirmDialogModel.ConfirmResult.OK) return;
        this.executeForceLogout(member.email);
      });
  }

  protected forceResetPassword(member: IntelligenceModels.Organizations.Member): void {
    const [eligibleMember] = this.getResetPasswordEligibleMembers([member]);
    if (!eligibleMember?.email) {
      return;
    }

    this.confirmDialogService
      .open({
        type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
        title: 'USERS.CONFIRMATIONS.FORCE_RESET_PASSWORD_SINGLE.TITLE',
        msg: 'USERS.CONFIRMATIONS.FORCE_RESET_PASSWORD_SINGLE.MSG',
        confirm: 'USERS.CONFIRMATIONS.FORCE_RESET_PASSWORD_SINGLE.CONFIRM',
        disableClose: true,
        params: { email: eligibleMember.email },
      })
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result?.selection !== ConfirmDialogModel.ConfirmResult.OK) return;
        this.executeForceResetPassword(eligibleMember.email);
      });
  }

  protected openEditModal(member: IntelligenceModels.Organizations.Member): void {
    const availableGroups = this.availableGroups();
    const permissionGroupsMap = availableGroups.reduce<
      Dictionary<PermissionGroupModels.PermissionGroupDocumentBase>
    >((lookup, group) => {
      lookup[group._id] = group;
      return lookup;
    }, {});

    const data: UserEditModalData = {
      user: toOrganizationMember(member),
      availableGroups,
      permissionGroupsMap,
      userGroupIds: [...member.groupIds],
      isEnterpriseOrg: !!this.activeOrganization()?.isEnterprise,
    };

    // The modal dispatches the update itself; the panel row is patched via the
    // `updateOrganizationMemberServerCallSuccess` listener in IntelligenceResultsService.
    this.dialog.open(UserEditModalComponent, {
      data,
      panelClass: 'modal-no-padding',
      width: '90vw',
      maxWidth: '500px',
    });
  }

  /**
   * Open the member's permissions page in a new tab (walls-result precedent), so
   * the Cmd+K session stays open — unlike the settings users page, which navigates.
   */
  protected openUserPermissionsPage(member: IntelligenceModels.Organizations.Member): void {
    if (!this.canManageUserPermissions()) {
      return;
    }
    const tree = this.router.createUrlTree([
      '/',
      routerSegments.settings,
      routerSegments.permissions,
      member.id,
      routerSegments.userPermissions,
    ]);
    const path = this.router.serializeUrl(tree);
    const url = window.location?.origin ? `${window.location.origin}${path}` : path;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  protected deleteMember(member: IntelligenceModels.Organizations.Member): void {
    if (!this.canRemoveUser()) {
      return;
    }
    const isOwn = member.id === this.currentUserId();

    this.confirmDialogService
      .open({
        type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
        title: 'DELETE_USER_MODAL.TITLE',
        msg: 'DELETE_USER_MODAL.MSG',
        confirm: 'DELETE_USER_MODAL.CONFIRM',
        disableClose: true,
        params: { email: member.email },
      })
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result?.selection !== ConfirmDialogModel.ConfirmResult.OK) return;
        if (isOwn) {
          this.store$.dispatch(
            OrganizationActions.leaveOrg({ orgId: this.activeOrganization()?.orgId ?? '' }),
          );
        } else {
          this.store$.dispatch(OrganizationActions.removeUser({ userId: member.id }));
        }
      });
  }

  // ── Bulk actions ────────────────────────────────────────────────────────────

  protected forceLogoutForSelectedMembers(): void {
    const emails = this.getForceLogoutEligibleMembers(this.selectedMembers())
      .map((member) => member.email)
      .filter((email): email is string => !!email);
    const selectedAll = this.areAllSelectedMembersChecked();

    if (!selectedAll && !emails.length) {
      return;
    }

    this.confirmDialogService
      .open({
        type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
        title: 'USERS.CONFIRMATIONS.FORCE_LOGOUT_BULK.TITLE',
        msg: selectedAll
          ? 'USERS.CONFIRMATIONS.FORCE_LOGOUT_BULK.MSG_ALL'
          : 'USERS.CONFIRMATIONS.FORCE_LOGOUT_BULK.MSG_COUNT',
        confirm: 'USERS.CONFIRMATIONS.FORCE_LOGOUT_BULK.CONFIRM',
        disableClose: true,
        params: { count: emails.length, suffix: emails.length === 1 ? '' : 's' },
      })
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result?.selection !== ConfirmDialogModel.ConfirmResult.OK) return;
        this.organizationService
          .forceLogoutByEmails(emails, selectedAll)
          .pipe(take(1))
          .subscribe({
            next: () => {
              this.store$.dispatch(
                SharedActions.showMessage({ success: 'Force logout started for selected users' }),
              );
              this.clearSelection();
            },
            error: () => {
              this.store$.dispatch(
                SharedActions.showMessage({ error: 'Failed to force logout selected users' }),
              );
            },
          });
      });
  }

  protected forceResetPasswordForSelectedMembers(): void {
    const emails = this.getResetPasswordEligibleMembers(this.selectedMembers())
      .map((member) => member.email)
      .filter((email): email is string => !!email);
    const selectedAll = this.areAllSelectedMembersChecked();

    if (!selectedAll && !emails.length) {
      return;
    }

    this.confirmDialogService
      .open({
        type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
        title: 'USERS.CONFIRMATIONS.FORCE_RESET_PASSWORD_BULK.TITLE',
        msg: selectedAll
          ? 'USERS.CONFIRMATIONS.FORCE_RESET_PASSWORD_BULK.MSG_ALL'
          : 'USERS.CONFIRMATIONS.FORCE_RESET_PASSWORD_BULK.MSG_COUNT',
        confirm: 'USERS.CONFIRMATIONS.FORCE_RESET_PASSWORD_BULK.CONFIRM',
        disableClose: true,
        params: { count: emails.length, suffix: emails.length === 1 ? '' : 's' },
      })
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result?.selection !== ConfirmDialogModel.ConfirmResult.OK) return;
        this.organizationService
          .forceResetPasswordByEmails(emails, selectedAll)
          .pipe(take(1))
          .subscribe({
            next: () => {
              this.store$.dispatch(
                SharedActions.showMessage({
                  success: 'Force reset password started for selected users',
                }),
              );
              this.clearSelection();
            },
            error: () => {
              this.store$.dispatch(
                SharedActions.showMessage({
                  error: 'Failed to enable password reset requirement for selected users',
                }),
              );
            },
          });
      });
  }

  // ── Private helpers ─────────────────────────────────────────────────────────

  private executeForceLogout(email: string): void {
    if (!this.canForceLogoutUser() || this.forceLogoutInProgressByEmail()[email]) {
      return;
    }

    this.markInProgress(this.forceLogoutInProgressByEmail, email, true);

    this.organizationService
      .forceLogoutByEmail(email)
      .pipe(
        take(1),
        finalize(() => this.markInProgress(this.forceLogoutInProgressByEmail, email, false)),
      )
      .subscribe({
        next: () => {
          this.store$.dispatch(SharedActions.showMessage({ success: `Forced logout for ${email}` }));
        },
        error: () => {
          this.store$.dispatch(
            SharedActions.showMessage({ error: `Failed to force logout for ${email}` }),
          );
        },
      });
  }

  private executeForceResetPassword(email: string): void {
    this.markInProgress(this.resetPasswordInProgressByEmail, email, true);

    this.organizationService
      .forceResetPasswordByEmail(email)
      .pipe(
        take(1),
        finalize(() => this.markInProgress(this.resetPasswordInProgressByEmail, email, false)),
      )
      .subscribe({
        next: () => {
          this.store$.dispatch(
            SharedActions.showMessage({ success: `Password reset required enabled for ${email}` }),
          );
        },
        error: () => {
          this.store$.dispatch(
            SharedActions.showMessage({
              error: `Failed to enable password reset requirement for ${email}`,
            }),
          );
        },
      });
  }

  private getResetPasswordEligibleMembers(
    members: IntelligenceModels.Organizations.Member[],
  ): IntelligenceModels.Organizations.Member[] {
    if (!this.canForcePasswordResetUser()) {
      return [];
    }
    return members.filter(
      (member) => !!member.email && !this.resetPasswordInProgressByEmail()[member.email],
    );
  }

  private getForceLogoutEligibleMembers(
    members: IntelligenceModels.Organizations.Member[],
  ): IntelligenceModels.Organizations.Member[] {
    if (!this.canForceLogoutUser()) {
      return [];
    }
    return members.filter(
      (member) => !!member.email && !this.forceLogoutInProgressByEmail()[member.email],
    );
  }

  private markInProgress(
    state: WritableSignal<Record<string, boolean>>,
    email: string,
    inProgress: boolean,
  ): void {
    state.update((current) => {
      const next = { ...current };
      if (inProgress) {
        next[email] = true;
      } else {
        delete next[email];
      }
      return next;
    });
  }

  private resetFilters(): void {
    const response = this.response();
    this.searchQuery.set(response?.filter ?? '');
    this.groupFilterControl.setValue(response?.selectedPermissionGroupIds ?? [], { emitEvent: false });
    this.sortState.set({ key: '', direction: OrganizationMembersModels.SortDirection.None });
    this.clearSelection();
  }
}
